import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useStaffAccess } from '@/hooks/useStaffAccess';
import { MyClassroomHeader } from '@/components/MyClassroomHeader';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Baby, Clock, Calendar, CheckCircle, Users, FileText, AlertCircle
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

import API from '@/config/api';
// API imported from config;

const ProviderDashboard = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [children, setChildren] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const access = useStaffAccess();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, childrenRes, attendanceRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, getAuthHeaders()),
        axios.get(`${API}/children`, getAuthHeaders()),
        axios.get(`${API}/attendance/today`, getAuthHeaders())
      ]);
      setStats(statsRes.data);
      setChildren(childrenRes.data);
      setTodayAttendance(attendanceRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async (childId) => {
    try {
      await axios.post(`${API}/attendance/check-in?child_id=${childId}`, {}, getAuthHeaders());
      toast.success('Check-in successful');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-in failed');
    }
  };

  const handleCheckOut = async (childId) => {
    try {
      await axios.post(`${API}/attendance/check-out?child_id=${childId}`, {}, getAuthHeaders());
      toast.success('Check-out successful');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-out failed');
    }
  };

  const myRooms = access?.classrooms;
  const checkedIn = children.filter((c) => c.is_checked_in).length;

  if (loading) {
    return (
      <DashboardLayout title="Teacher Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Teacher Dashboard">
      <div className="space-y-8" data-testid="provider-dashboard">
        <MyClassroomHeader access={access} childCount={children.length} checkedIn={checkedIn} />
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card 
            className="card-clay cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
            onClick={() => navigate('/children')}
          >
            <CardContent className="p-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-3">
                <Baby className="w-6 h-6" />
              </div>
              <p className="text-2xl font-bold" data-testid="my-children-count">{children.length}</p>
              <p className="text-sm text-muted-foreground">{myRooms ? 'My Children' : 'Total Children'}</p>
            </CardContent>
          </Card>
          <Card 
            className="card-clay cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
            onClick={() => navigate('/attendance')}
          >
            <CardContent className="p-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 text-green-600 flex items-center justify-center mb-3">
                <CheckCircle className="w-6 h-6" />
              </div>
              <p className="text-2xl font-bold" data-testid="my-checked-in-count">{checkedIn}</p>
              <p className="text-sm text-muted-foreground">Checked In</p>
            </CardContent>
          </Card>
          <Card 
            className="card-clay cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
            onClick={() => navigate('/attendance')}
          >
            <CardContent className="p-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center mb-3">
                <Users className="w-6 h-6" />
              </div>
              <p className="text-2xl font-bold" data-testid="my-not-checked-in-count">{children.length - checkedIn}</p>
              <p className="text-sm text-muted-foreground">Not Checked In</p>
            </CardContent>
          </Card>
          <Card 
            className="card-clay cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
            onClick={() => navigate('/messages')}
          >
            <CardContent className="p-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center mb-3">
                <AlertCircle className="w-6 h-6" />
              </div>
              <p className="text-2xl font-bold">{stats?.unread_messages || 0}</p>
              <p className="text-sm text-muted-foreground">Messages</p>
            </CardContent>
          </Card>
        </div>

        {/* Attendance Management */}
        <Card className="card-clay">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Today's Attendance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {children.length > 0 ? (
              <div className="grid gap-4">
                {children.map((child) => {
                  const attendance = todayAttendance.find(a => a.child_id === child.id);
                  const isCheckedIn = child.is_checked_in;
                  
                  return (
                    <div 
                      key={child.id}
                      className="flex items-center justify-between p-4 bg-muted/50 rounded-xl"
                      data-testid={`child-attendance-${child.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="font-bold text-primary">
                            {child.first_name.charAt(0)}{child.last_name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold">{child.first_name} {child.last_name}</p>
                          <p className="text-sm text-muted-foreground">{child.classroom}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          isCheckedIn 
                            ? 'bg-green-100 text-green-600' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {isCheckedIn ? 'Present' : 'Not Checked In'}
                        </span>
                        {isCheckedIn ? (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleCheckOut(child.id)}
                            data-testid={`checkout-btn-${child.id}`}
                          >
                            Check Out
                          </Button>
                        ) : (
                          <Button 
                            size="sm"
                            onClick={() => handleCheckIn(child.id)}
                            data-testid={`checkin-btn-${child.id}`}
                          >
                            Check In
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Baby className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No children enrolled yet</p>
                <Button 
                  className="mt-4"
                  onClick={() => window.location.href = '/children'}
                >
                  Add Children
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4">
          <Button 
            variant="outline" 
            className="h-auto py-6 flex flex-col items-center gap-2 card-clay"
            onClick={() => window.location.href = '/daily-reports'}
            data-testid="quick-action-reports"
          >
            <FileText className="w-8 h-8 text-primary" />
            <span className="font-semibold">Create Daily Report</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-6 flex flex-col items-center gap-2 card-clay"
            onClick={() => window.location.href = '/messages'}
            data-testid="quick-action-messages"
          >
            <Users className="w-8 h-8 text-primary" />
            <span className="font-semibold">Message Parents</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-6 flex flex-col items-center gap-2 card-clay"
            onClick={() => window.location.href = '/attendance'}
            data-testid="quick-action-attendance"
          >
            <Calendar className="w-8 h-8 text-primary" />
            <span className="font-semibold">View Attendance</span>
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ProviderDashboard;
